import React from 'react';
import { FlowNode } from '../../../../types/flowBuilder';
import { BaseConfigProps, ConfigSection, ConfigField } from '../shared/ConfigFormHelpers';

interface JSONProcessorConfigProps extends BaseConfigProps {
  node: FlowNode;
  updateConfig: (key: string, value: any) => void;
}

const JSONProcessorConfig: React.FC<JSONProcessorConfigProps> = ({ node, updateConfig }) => {
  return (
    <div className="space-y-4">
      <ConfigSection title="JSON feldolgozás">
        <ConfigField
          label="Művelet típusa"
          description="Mit szeretnél csinálni a JSON-nal"
        >
          <select
            value={node.data.config?.operation || 'parse'}
            onChange={(e) => updateConfig('operation', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="parse">Szövegből JSON (parse)</option>
            <option value="stringify">JSON-ból szöveg (stringify)</option>
            <option value="get">Érték lekérése (path)</option>
            <option value="set">Érték beállítása (path)</option>
            <option value="merge">Objektumok egyesítése</option>
            <option value="validate">Validálás (JSON Schema)</option>
            <option value="transform">Transzformálás (JMESPath)</option>
          </select>
        </ConfigField>

        {node.data.config?.operation === 'parse' && (
          <ConfigField
            label="JSON szöveg"
            description="A feldolgozandó JSON string"
          >
            <textarea
              value={node.data.config?.jsonString || ''}
              onChange={(e) => updateConfig('jsonString', e.target.value)}
              placeholder='{"name": "John", "age": 30}'
              rows={5}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white font-mono text-sm"
            />
          </ConfigField>
        )}

        {node.data.config?.operation === 'stringify' && (
          <>
            <ConfigField
              label="JSON objektum"
              description="Az objektum változó neve"
            >
              <input
                type="text"
                value={node.data.config?.jsonObject || ''}
                onChange={(e) => updateConfig('jsonObject', e.target.value)}
                placeholder="{{data}} vagy data"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              />
            </ConfigField>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="prettyPrint"
                checked={node.data.config?.prettyPrint || false}
                onChange={(e) => updateConfig('prettyPrint', e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="prettyPrint" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Formázott kimenet (pretty print)
              </label>
            </div>
          </>
        )}

        {(node.data.config?.operation === 'get' || node.data.config?.operation === 'set') && (
          <>
            <ConfigField
              label="JSON objektum"
              description="A feldolgozandó objektum"
            >
              <input
                type="text"
                value={node.data.config?.jsonObject || ''}
                onChange={(e) => updateConfig('jsonObject', e.target.value)}
                placeholder="{{data}} vagy data"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              />
            </ConfigField>

            <ConfigField
              label="JSON Path"
              description="Az elérési út (pl. user.address.city)"
            >
              <input
                type="text"
                value={node.data.config?.path || ''}
                onChange={(e) => updateConfig('path', e.target.value)}
                placeholder="user.address.city vagy items[0].name"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              />
            </ConfigField>

            {node.data.config?.operation === 'set' && (
              <ConfigField
                label="Új érték"
                description="A beállítandó érték"
              >
                <input
                  type="text"
                  value={node.data.config?.value || ''}
                  onChange={(e) => updateConfig('value', e.target.value)}
                  placeholder="Budapest vagy {{city}}"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </ConfigField>
            )}
          </>
        )}

        {node.data.config?.operation === 'merge' && (
          <>
            <ConfigField
              label="Első objektum"
              description="Az alap objektum"
            >
              <input
                type="text"
                value={node.data.config?.object1 || ''}
                onChange={(e) => updateConfig('object1', e.target.value)}
                placeholder="{{data1}}"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              />
            </ConfigField>

            <ConfigField
              label="Második objektum"
              description="Az egyesítendő objektum"
            >
              <input
                type="text"
                value={node.data.config?.object2 || ''}
                onChange={(e) => updateConfig('object2', e.target.value)}
                placeholder="{{data2}}"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              />
            </ConfigField>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="deepMerge"
                checked={node.data.config?.deepMerge !== false}
                onChange={(e) => updateConfig('deepMerge', e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="deepMerge" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Mély egyesítés (nested objektumok is)
              </label>
            </div>
          </>
        )}

        {node.data.config?.operation === 'transform' && (
          <>
            <ConfigField
              label="JSON objektum"
              description="A transzformálandó objektum"
            >
              <input
                type="text"
                value={node.data.config?.jsonObject || ''}
                onChange={(e) => updateConfig('jsonObject', e.target.value)}
                placeholder="{{data}}"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              />
            </ConfigField>

            <ConfigField
              label="JMESPath kifejezés"
              description="A transzformációs lekérdezés"
            >
              <textarea
                value={node.data.config?.jmesPath || ''}
                onChange={(e) => updateConfig('jmesPath', e.target.value)}
                placeholder="items[?price > `100`].{name: name, price: price}"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white font-mono text-sm"
              />
            </ConfigField>
          </>
        )}

        <div className="flex items-center">
          <input
            type="checkbox"
            id="failOnError"
            checked={node.data.config?.failOnError !== false}
            onChange={(e) => updateConfig('failOnError', e.target.checked)}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
          />
          <label htmlFor="failOnError" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            Hiba esetén flow megszakítása
          </label>
        </div>
      </ConfigSection>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
        <div className="text-xs text-blue-700 dark:text-blue-300">
          <strong>📦 JSON műveletek:</strong>
          <br />• <strong>parse:</strong> JSON szöveg → objektum
          <br />• <strong>stringify:</strong> Objektum → JSON szöveg
          <br />• <strong>get:</strong> Érték kinyerése path alapján
          <br />• <strong>merge:</strong> Két objektum egyesítése
          <br />• <strong>transform:</strong> Komplex lekérdezések (JMESPath)
        </div>
      </div>
    </div>
  );
};

export default JSONProcessorConfig;
